import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

import { useDeckCardThumbnails } from "@/cards/common/hooks/useDeckCardThumbnails";
import { getCardThumbnailPurchaseId } from "@/cards/common/types/app-deck/deckCardThumbnail.types";
import { useCurrentTalentProfile } from "@/shared";

import {
  useCreateMainCardContribution,
  useGetMainCardContributions,
  verifyImpactContribution,
} from "../api/impactContribution.api";
import type { ImpactCardData } from "../utils/impactCardData";
import { IMPACT_CARD_TYPE } from "../utils/impactCardType";
import { applyLiveContributions, mapImpactContributions } from "../utils/mapImpactContributions";

function toPositiveId(value: unknown): number | undefined {
  const id = Number(value);
  return Number.isFinite(id) && id > 0 ? id : undefined;
}

type UseImpactCardContributionsOptions = {
  mainCardId?: number | null;
  /** Talent who owns this Card — used so only the owner sends `talent-id`. */
  cardTalentId?: number | string | null;
  /** Collector copy of this Card — required for the user contributions list. */
  purchaseId?: number | string | null;
  enabled?: boolean;
};

function apiErrorMessage(error: unknown): string {
  const data = (error as { response?: { data?: Record<string, unknown> } })?.response?.data;
  const message = data?.message ?? data?.detail ?? data?.error;
  if (typeof message === "string" && message.trim()) return message.trim();
  if (Array.isArray(message) && typeof message[0] === "string") return message[0];
  return (error as Error)?.message || "Contribution failed. Try again.";
}

export function useImpactCardContributions({
  mainCardId,
  cardTalentId,
  purchaseId,
  enabled = true,
}: UseImpactCardContributionsOptions) {
  const queryClient = useQueryClient();
  const { currentProfile } = useCurrentTalentProfile();

  const cardId = Number(mainCardId) || 0;
  const viewerTalentId = currentProfile?.id;
  const ownerTalentId = cardTalentId != null && String(cardTalentId).trim() ? String(cardTalentId) : "";
  const isOwnerViewer =
    Boolean(viewerTalentId) && Boolean(ownerTalentId) && String(viewerTalentId) === ownerTalentId;

  const { thumbnailsByCardId } = useDeckCardThumbnails({
    type: IMPACT_CARD_TYPE,
    enabled: enabled && !isOwnerViewer && cardId > 0,
  });
  const thumbnailPurchaseId = getCardThumbnailPurchaseId(thumbnailsByCardId.get(String(cardId)));
  const ownPurchaseId = thumbnailPurchaseId ?? toPositiveId(purchaseId);
  const canQuery = enabled && (isOwnerViewer ? cardId > 0 : ownPurchaseId != null);

  const query = useGetMainCardContributions({
    key: [
      "main-card-contributions",
      isOwnerViewer ? "owner" : "collector",
      isOwnerViewer ? cardId : ownPurchaseId,
    ],
    params: isOwnerViewer
      ? {
          main_card_id: cardId,
          offset: 0,
          limit: 100,
          headers: { "talent-id": ownerTalentId },
        }
      : {
          purchase_id: ownPurchaseId,
          offset: 0,
          limit: 100,
        },
    options: { enabled: canQuery },
  });

  const { update, isLoading: isSubmitting } = useCreateMainCardContribution({
    key: ["create-main-card-contribution", cardId],
  });
  const [verifyingId, setVerifyingId] = useState<string | number | null>(null);

  const mapped = useMemo(() => mapImpactContributions(query.data), [query.data]);

  const contribute = async (amount: number, message: string) => {
    if (!cardId) {
      throw new Error("This Card is unavailable.");
    }

    try {
      await update({
        main_card_id: cardId,
        amount,
        message: message.trim(),
        purchase_type: "contribution",
        status: "committed",
      });
    } catch (error) {
      throw new Error(apiErrorMessage(error));
    }

    await queryClient.invalidateQueries({ queryKey: ["main-card-contributions"] });
    enqueueSnackbar("Thank you. Your commitment was recorded. It will count toward the goal after verification.", {
      variant: "success",
    });
  };

  const verify = async (input: {
    contributionId: string | number;
    purchaseId?: string | number;
    status: "Verified" | "Rejected";
    verifiedAmount?: number;
    note?: string;
  }) => {
    if (!ownerTalentId) {
      enqueueSnackbar("Only the card creator can verify contributions.", { variant: "error" });
      return;
    }
    const contributionId = toPositiveId(input.contributionId);
    if (!contributionId) {
      enqueueSnackbar("This contribution cannot be verified.", { variant: "error" });
      return;
    }
    setVerifyingId(input.contributionId);
    try {
      await verifyImpactContribution({
        contributionId,
        talentId: ownerTalentId,
        status: input.status,
        verifiedAmount: input.verifiedAmount,
        note: input.note,
      });
      await queryClient.invalidateQueries({ queryKey: ["main-card-contributions"] });
      enqueueSnackbar(
        input.status === "Verified" ? "Contribution verified." : "Contribution rejected.",
        { variant: "success" }
      );
    } catch (error) {
      enqueueSnackbar(apiErrorMessage(error), { variant: "error" });
    } finally {
      setVerifyingId(null);
    }
  };

  const applyTo = (data: ImpactCardData): ImpactCardData =>
    applyLiveContributions(data, mapped, query.isFetched);

  return {
    contributors: mapped.contributors,
    contributorCount: mapped.total,
    verifiedFund: mapped.verifiedTotal,
    committedFund: mapped.committedTotal,
    fetched: query.isFetched,
    isSubmitting,
    contribute,
    verify,
    verifyingId,
    applyTo,
  };
}
